import * as React from 'react'
import {Drawer, Box, Typography, Chip, IconButton, Divider, Button, ButtonBase, Tooltip} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import {primaryBtnSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, INFO, WARNING, SUCCESS, ERROR, MUTED, PURPLE, withOpacity} from '../../../styles/foundation/colors'
import {STATUS} from './data/reference'
import {fmtDate, fmtKg, fmtCurrency, deadline} from './format'
import {availableActions, ACTION_LABELS, disputeBlockedReason} from './actions'
import Lightbox from './Lightbox'
import {WeightComparisonSection, EvidenceSection, sectionHeadingSx} from './RecordSections'
import EvidenceIntake from './EvidenceIntake'
import {courierScanSrcs} from './data/scanImage'

// Wide enough for the scan and the weight comparison to sit side by side with
// neither cramped: SCAN_W + the comparison's own label/value rows + padding.
const DRAWER_W = 780
const SCAN_W = 300

// A floor only: the weight cards normally set the height, but a short card
// stack must not squash the scan to nothing.
const SCAN_MIN_H = 150

// Same tone vocabulary as DisputeGrid.js's TONE_COLOR — kept local because
// nothing outside status chips ever needs it.
const TONE_COLOR = {info: INFO, ai: PURPLE, warn: WARNING, neutral: MUTED, ok: SUCCESS, danger: ERROR}
const DEADLINE_COLOR = {crit: ERROR, warn: WARNING, ok: SUCCESS}

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/**
 * Row detail drawer. `row` is a live row from index.js's mutable dispute
 * state (not the frozen DISPUTES export) — see actions.js for why that
 * distinction matters: every action below only ever changes that local copy.
 *
 * `onAct(actionType)` is fired for whichever footer button the merchant
 * clicks; index.js owns turning that into a useDisputeActions() call (confirm
 * dialog + toast + state update) for the currently open row. `onSubmitEvidence
 * (rowId, payload)` is the same kind of thin pass-through for EvidenceIntake's
 * own Submit — index.js applies it via state/evidenceIntake.js's
 * applyEvidenceIntake, the same local-only-state contract as
 * EvidenceStudio.js's onSubmit.
 *
 * `focusFallbackRef` — same mechanism the Freeze module's CataloguePage/
 * PackingPanel already use (see their own doc comments): this drawer's own
 * trigger (a row's "Dispute"/"View" button in DisputeGrid.js) can vanish the
 * instant a footer action changes that row's status and it drops out of the
 * active tab's filtered set, which happens before MUI's own focus-restore
 * runs. When that leaves focus on <body>, fall back to the active tab
 * instead of letting keyboard focus disappear.
 */
export default function DisputeDrawer({row, open, onClose, onAct, onSubmitEvidence, focusFallbackRef}) {
  const [lightboxSrc, setLightboxSrc] = React.useState(null)

  const handleCloseDrawer = () => {
    setLightboxSrc(null)
    onClose()
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }

  if (!row) {
    return <Drawer anchor='right' open={open} onClose={onClose} />
  }

  const statusDef = STATUS[row.status] || {}
  // 'action' is the only group a dispute can still be raised from — NEW rows.
  // Disputed rows are with the courier and resolved rows are closed.
  const canAddEvidence = statusDef.group === 'action'
  const tone = TONE_COLOR[statusDef.tone] || NAVY
  const dl = deadline(row.deadlineAt)
  // Only the FIRST scan: the drawer shows one image, and the viewer opened from
  // it pages through the rest if a row ever carries more. On a live row this is
  // the courier's real photo (data/scanImage.js#courierScanSrcs).
  const scanSrc = courierScanSrcs(row)[0] || null
  const actions = availableActions(row.status)
  // The footer used to offer an enabled Dispute button while the evidence
  // form directly above it correctly refused to submit — so the merchant
  // could walk past the form and dispute with nothing attached. One rule,
  // read by both controls: see actions.js's disputeBlockedReason.
  const disputeBlocked = disputeBlockedReason(row)

  return (
    <>
      {/* anchor='right', full width below sm. Widened past the original 480px
          so the parcel scan and the weight comparison can sit side by side
          instead of the comparison being read a screen away from the photo it
          describes — they are the two halves of one question. 560px on sm
          keeps the single-column stack for tablets. Escape
          and focus-return are both handled by MUI's underlying Modal — see Lightbox.js
          for the same note. */}
      <Drawer
        anchor='right'
        open={open}
        onClose={handleCloseDrawer}
        PaperProps={{sx: {width: {xs: '100%', sm: 560, md: DRAWER_W}}}}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <Box sx={{p: 2.5, borderBottom: `1px solid ${BORDER}`}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
              <Box>
                <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.extra, fontSize: SIZE.md, color: NAVY}}>
                  {row.awb}
                </Typography>
                <Typography sx={labelSx}>{row.orderId} · {row.courier} · {row.service}</Typography>
              </Box>
              <IconButton onClick={handleCloseDrawer} aria-label='Close dispute details' size='small'>
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center', mt: 1.5}}>
              <Chip
                size='small'
                label={statusDef.label || row.status}
                sx={{bgcolor: withOpacity(tone, 0.12), color: tone, fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs']}}
              />
              {/* The countdown only means something while the merchant can still act.
                  On a closed row the backend reuses the deadline field as the closing
                  timestamp (v1 behaviour), so a resolved dispute would otherwise read
                  "Past due" forever. */}
              {statusDef.group !== 'resolved' && (
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: DEADLINE_COLOR[dl.level]}}>
                  {dl.label}
                </Typography>
              )}
              {/* Item 3: Prozo settled between the two weights. The figures come from the
                  merchant's own rate card at the settled weight — nothing here is derived. */}
              {row.status === 'SETTLED_PROSHIP' && row.settledKg != null && (
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: WARNING}}>
                  Settled at {fmtKg(row.settledKg)} · you paid {fmtCurrency(row.settledCharge || 0)}
                  {row.settledReleased ? ` · ${fmtCurrency(row.settledReleased)} released back to your wallet` : ''}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{p: 2.5, overflowY: 'auto', flex: 1}}>
            {/* The courier's scan beside the weights it produced. Stacks below
                md, where two columns would leave neither enough room. The
                dataset has no product photography — this is the parcel scan
                from data/scanImage.js, the same image the grid's Courier image
                link opens, so the two surfaces never show different photos of
                the same shipment. */}
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: `${SCAN_W}px minmax(0, 1fr)`}, gap: 2.5, alignItems: 'stretch'}}>
              {/* A flex column so the scan can fill whatever height the weight
                  cards beside it set, instead of running past them at its own
                  aspect ratio and leaving the band visibly uneven. */}
              <Box sx={{display: 'flex', flexDirection: 'column', minHeight: 0}}>
                <Typography sx={{...sectionHeadingSx, mb: 1}}>Courier scan</Typography>
                {scanSrc ? (
                  <ButtonBase
                    onClick={() => setLightboxSrc(scanSrc)}
                    aria-label={`View the courier weight scan for AWB ${row.awb} full size`}
                    // flex:1 claims the space left after the heading; position
                    // relative anchors the image, which is taken out of flow so
                    // its natural aspect cannot drive the row height — the cards
                    // decide it, and the scan matches.
                    sx={{flex: 1, minHeight: SCAN_MIN_H, position: 'relative', width: '100%', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${BORDER}`, display: 'block'}}
                  >
                    <Box
                      component='img'
                      src={scanSrc}
                      alt=''
                      // cover, anchored to the TOP: the readings the merchant
                      // actually reads — hub, AWB, dimensions, weight — are
                      // printed at the top of the panel, so any crop has to come
                      // off the package graphic at the bottom, never off them.
                      // Full detail is one click away in the viewer regardless.
                      sx={{position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block'}}
                    />
                  </ButtonBase>
                ) : (
                  <Box sx={{flex: 1, minHeight: SCAN_MIN_H, border: `1px dashed ${BORDER}`, borderRadius: '8px', display: 'grid', placeItems: 'center', p: 2}}>
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: MUTED}}>
                      No courier scan on file
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Wrapped in a Box on purpose. WeightComparisonSection returns
                  a FRAGMENT, so without this its ten label/value rows become
                  grid items in their own right and zig-zag across both columns
                  — "Original weight" under the image, "Applied weight" beside
                  it, and so on. The Box makes the whole section one item. */}
              <Box>
                <WeightComparisonSection row={row} />
              </Box>
            </Box>

            <Divider sx={{my: 2}} />

            {/* The courier thumbnails are suppressed here: the scan is already
                shown full width above, and a row never carries more than one in
                this dataset, so the strip would repeat the same photo. */}
            <EvidenceSection row={row} onImageClick={setLightboxSrc} hideCourierScans />

            {/* The capture form only exists while a dispute can still be
                raised. Once one is submitted the row shows what was sent and
                nothing more: a second upload cannot reach a case already with
                the courier, so offering the form would invite work that goes
                nowhere. EvidenceSection above is the read-only record. */}
            {canAddEvidence && (
              <Box sx={{mt: 1.5, mb: 2}}>
                <EvidenceIntake row={row} onSubmit={onSubmitEvidence} />
              </Box>
            )}

            <Divider sx={{my: 2}} />

            <Typography sx={sectionHeadingSx}>Status history</Typography>
            <Timeline sx={{p: 0, m: 0, '& .MuiTimelineItem-root:before': {flex: 0, padding: 0}}}>
              {(row.statusLog || []).map((entry, i) => {
                const entryDef = STATUS[entry.status] || {}
                const entryTone = TONE_COLOR[entryDef.tone] || MUTED
                return (
                  <TimelineItem key={i}>
                    <TimelineSeparator>
                      <TimelineDot sx={{bgcolor: entryTone}} />
                      {i < row.statusLog.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent sx={{pb: 2}}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY}}>
                        {entryDef.label || entry.status}
                      </Typography>
                      {/* The smallest step on the scale. This was an off-scale
                          literal, a size nothing else in either module used;
                          colour and weight are what keep it subordinate to the
                          status word above it. */}
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
                        {fmtDate(entry.at)} · {entry.by}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                )
              })}
            </Timeline>
          </Box>

          <Box sx={{p: 2, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap'}}>
            {actions.map((actionType) => {
              if (actionType === 'dispute' && disputeBlocked) {
                return (
                  // A disabled MUI Button fires no pointer events and takes no
                  // focus, so the reason cannot live on the button itself — a
                  // bare `title` would be unreachable by keyboard. The wrapper
                  // is focusable and carries role='note', the same shape
                  // SkuFreezeDrawer.js's blocked Request button uses.
                  <Tooltip key={actionType} title={disputeBlocked}>
                    <Box
                      component='span'
                      tabIndex={0}
                      role='note'
                      aria-label={'Disputing is unavailable. ' + disputeBlocked}
                      sx={{display: 'inline-flex'}}
                    >
                      <Button disabled sx={primaryBtnSx}>{ACTION_LABELS[actionType]}</Button>
                    </Box>
                  </Tooltip>
                )
              }
              return (
                <Button
                  key={actionType}
                  onClick={() => onAct(actionType)}
                  sx={actionType === 'dispute' || actionType === 'accept' ? primaryBtnSx : secondaryBtnSx}
                >
                  {ACTION_LABELS[actionType]}
                </Button>
              )
            })}
          </Box>
        </Box>
      </Drawer>

      <Lightbox
        src={lightboxSrc}
        alt={`Courier scan for AWB ${row.awb}`}
        open={!!lightboxSrc}
        onClose={() => setLightboxSrc(null)}
      />

    </>
  )
}
